import { useState } from "react";
import { useIntl } from "react-intl";
import { OverlayTrigger, Popover } from "@rio-cloud/rio-uikit";
import { Scheduling } from "../../models/schedule";
import { useNavigate } from "react-router-dom";
import DeleteAppointmentDialog from "./dialogs/deleteAppointmentDialog";
import { useMediaQuery } from "@mui/material";
import VehicleIcon from "../VehicleIcon";
export type userType = "fleet-manager" | "dealership" | "control-tower";

interface IAppointmentCardProps {
  schedule?: Scheduling;
  userType: userType;
}

const AppointmentCard = (props: IAppointmentCardProps) => {
  const intl = useIntl();
  const placements = ["left"] as const;
  const navigate = useNavigate();
  const [deleteAppointmentDialog, setDeleteAppointmentDialog] =
    useState<boolean>(false);
  const isResponsiveView = useMediaQuery("(max-width: 1023px)");
  return (
    <>
      <div
        key={props.schedule?.id}
        className="bg-white dark:bg-neutral-800 p-6 w-full margin-top-25 full-schedule"
      >
        <div className="flex padding-top-10 justify-between items-center mb-4">
          <div className="flex items-center space-x-2 w-9/12 gap-8">
            <span
              className={`text-size-18 font-semibold text-color-map-marker-asset flex items-center ${isResponsiveView ? 'w-full' : 'w-3/12'} truncate`}
            >
              <span className="rioglyph rioglyph-calendar text-color-map-marker-asset"></span>
              {props.schedule?.date.toLocaleDateString(intl.locale, {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </span>

            {!isResponsiveView ? (
              <div className="flex gap-6 schedule-tags w-9/12 media_leftRow">
                <div className="flex items-center justify-center gap-5 schedule-tags__card">
                  <span className="rioglyph rioglyph-time-alt"></span>
                  <span>
                    {props.schedule?.date.toLocaleTimeString(intl.locale, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>

                <div className="flex items-center justify-center gap-5 schedule-tags__card truncate">
                  <span className="rioglyph rioglyph-workshop padding-x-15 margin-right-5"></span>
                  <span className="truncate margin-right-10">
                    {props.schedule?.dealership?.name}
                  </span>
                </div>

                <div className="flex items-center justify-center gap-5 schedule-tags__card truncate">
                  <span className="rioglyph rioglyph-pin padding-x-15 margin-right-5"></span>
                  <span className="truncate margin-right-10">
                    {props.schedule?.dealership?.address}
                  </span>
                </div>
              </div>
            ) : null}
          </div>
          <div className="w-3/12 flex items-center gap-6 justify-end">
            {!isResponsiveView ? (
              <span
                className="flex items-center justify-center rounded-full payment-info text-center"
                style={{ marginBottom: 0, marginTop: 2 }}
              >
                {intl.formatMessage({ id: "intl-msg:appointmentCard.appointmentCard.paymentAtDealership" })}
              </span>
            ) : null}
            {placements.map((placement) => (
              <OverlayTrigger
                trigger="click"
                key={placement}
                placement={placement}
                overlay={
                  <Popover id={`popover-positioned-${placement}`}>
                    {props.schedule?.scheduleState === 'AC' && (
                      <div
                        className="popover-item flex items-center cursor-pointer"
                        onClick={() =>
                          navigate(
                            `/${props.userType}/appointments/appointment?scheduling-id=${props.schedule?.id}`
                          )
                        }
                      >
                        <span className="text-color-darkest rioglyph rioglyph-calendar"></span>
                        <span className="text-color-darkest text-size-14 font-semibold">
                          {intl.formatMessage({ id: "intl-msg:appointmentCard.appointmentCard.reschedule" })}
                        </span>
                      </div>
                    )}

                    <div
                      className="popover-item flex items-center cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/${props.userType}/appointments/${props.schedule?.id}`
                        )
                      }
                    >
                      <span className="text-color-darkest text-size-h4 rioglyph rioglyph-eye-option"></span>
                      <span className="text-color-darkest text-size-14 font-semibold">
                        {intl.formatMessage({ id: "intl-msg:appointmentCard.appointmentCard.viewDetails" })}
                      </span>
                    </div>

                    {props.schedule?.scheduleState === 'AC' && (
                      <div
                        className="popover-item flex items-center cursor-pointer"
                        onClick={() => setDeleteAppointmentDialog(true)}
                      >
                        <span className="text-color-darkest text-size-h4 rioglyph rioglyph-remove-circle"></span>
                        <span className="text-color-darkest text-size-14 font-semibold">
                          {intl.formatMessage({ id: "intl-msg:appointmentCard.appointmentCard.cancel" })}
                        </span>
                      </div>
                    )}
                  </Popover>
                }
              >
                <div>
                  <span className="rioglyph rioglyph-option-vertical cursor-pointer"></span>
                </div>
              </OverlayTrigger>
            ))}{" "}
          </div>
        </div>

        <hr className="border-gray-200 dark:border-neutral-700 my-2" />

        <div className="flex items-start justify-between mb-4 padding-top-25">
          {!isResponsiveView ? (
            <div className="flex space-x-2 w-9/12">
              <div className="flex flex-col pr-10">
                <div className="flex items-center justify-center gap-6 payment-method">
                  <span className="text-size-16 font-bold text-color-map-marker-asset">
                    {intl.formatMessage({ id: "intl-msg:appointmentCard.appointmentCard.amountToPay" })}
                  </span>
                  <span className="text-size-14 font-bold flex items-center justify-around price-value">
                    {props.schedule?.totalPrice}
                  </span>
                </div>

                <div className="flex flex-col padding-top-25">
                  {props.schedule?.comments ? (
                    <>
                      <span className="text-size-14 font-semibold text-color-highlight-darkest">
                        {intl.formatMessage({ id: "intl-msg:appointmentCard.appointmentCard.important" })}
                      </span>
                      <span className="text-size-14 font-normal text-color-highlight-darkest">
                        {props.schedule?.comments}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col padding-right-50 media_leftRow">
                <span className="text-size-14 font-semibold text-color-highlight-darkest">
                  {intl.formatMessage({ id: "intl-msg:appointmentCard.appointmentCard.requester" })}
                </span>
                <span className="text-size-14 font-normal text-color-highlight-darkest">
                  {props.schedule?.scheduledBy || props.schedule?.consultant}
                </span>
              </div>
            </div>
          ) : null}
          <div
            className={`flex items-center ${
              isResponsiveView ? "!w-full" : "w-3/12 justify-end"
            }`}
          >
            <div className="flex gap-6 items-center">
              <VehicleIcon 
                chassis={props.schedule?.asset?.chassis} 
                size={64}
              />
              <div className="flex flex-col">
                <span className="text-size-14 font-semibold text-color-highlight-darkest">
                  {props.schedule?.asset?.name}
                </span>
                <span className="text-size-14 font-normal text-text-color-darker">
                  {intl.formatMessage(
                    { id: "intl-msg:appointmentCard.appointmentCard.chassis" },
                    { chassis: props.schedule?.asset?.chassis }
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="display-flex gap-20 margin-top-25 margin-bottom-15">
          <button
            type="button"
            className={`btn btn-secondary ${
              isResponsiveView ? "w-full" : "width-250"
            }`}
            onClick={() =>
              navigate(
                `/${props.userType}/maintenance-monitoring?chassi=${props.schedule?.asset?.chassis}`
              )
            }
          >
            {intl.formatMessage({ id: "intl-msg:appointmentCard.appointmentCard.trackMaintenance" })}
          </button>
        </div>
      </div>

      <DeleteAppointmentDialog
        scheduleId={props.schedule?.id}
        showDialog={deleteAppointmentDialog}
        handleClose={() => setDeleteAppointmentDialog(false)}
        userType={props.userType}
      />
    </>
  );
};

export default AppointmentCard;
